import { useOrganisation } from "@hooks/crud/organisation/useOrganisation";
import { useProfile } from "@hooks/crud/profile/useProfile";
import { update } from "@intercom/messenger-js-sdk";
import { setSentryUser } from "@utils/sentry";
import React, {
  createContext,
  useContext,
  useEffect,
  useCallback
} from "react";
import { OrganisationLineItem } from "catchment-common/interfaces/organisation";
import { ProfileLineItem } from "catchment-common/interfaces/profile";

import { useAuthContext } from "./useAuthContext";

type ProfileContextType = {
  userProfile?: ProfileLineItem;
  userOrganisation?: OrganisationLineItem;
  error?: string;
  isAuthProfileLoading: boolean;
  setUserOrganisation: (organisation: OrganisationLineItem) => void;
  createUserProfile: (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => Promise<void>;
  setUpUserProfileAndOrg: (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => Promise<void>;
  updateUserProfile: (profile: ProfileLineItem) => Promise<void>;
  updateUserOrganisation: (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => Promise<void>;
};

export const ProfileContext = createContext<ProfileContextType>({
  createUserProfile: () => Promise.resolve(),
  error: "Context not initialised correctly.",
  isAuthProfileLoading: false,
  setUpUserProfileAndOrg: () => Promise.resolve(),
  setUserOrganisation: () => Promise.resolve(),
  updateUserOrganisation: () => Promise.resolve(),
  updateUserProfile: () => Promise.resolve()
});

type ProfileProviderProps = {
  children: React.ReactNode;
};

const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const { isAuthLoading, setUserProfile, userProfile } = useAuthContext();
  const { createProfile, getProfile, updateProfile } = useProfile();
  const {
    createOrganisation,
    getProfileOrganisation,
    isOrganisationLoading,
    setUserOrganisation,
    updateOrganisation,
    userOrganisation
  } = useOrganisation();

  const createUserProfile = useCallback(
    async (organisation: OrganisationLineItem, profile: ProfileLineItem) => {
      await createOrganisation(organisation);
      const createdProfile = await createProfile(profile);
      setUserProfile(createdProfile);
    },
    [createOrganisation, createProfile, setUserProfile]
  );

  const setUpUserProfileAndOrg = useCallback(
    async (organisation: OrganisationLineItem, profile: ProfileLineItem) => {
      await updateOrganisation(organisation);
      await updateProfile(profile);
      const { profile: refreshedProfile } = await getProfile(profile.id, true);
      setUserProfile(refreshedProfile || profile);
    },
    [getProfile, setUserProfile, updateOrganisation, updateProfile]
  );

  const updateUserProfile = useCallback(
    async (profile: ProfileLineItem) => {
      await updateProfile(profile);
      const { profile: refreshedProfile } = await getProfile(profile.id, true);
      setUserProfile(refreshedProfile || profile);
    },
    [getProfile, updateProfile, setUserProfile]
  );

  const updateUserOrganisation = useCallback(
    async (organisation: OrganisationLineItem, profile: ProfileLineItem) => {
      if (profile.isAdmin) {
        await updateOrganisation(organisation);
      }
    },
    [updateOrganisation]
  );

  useEffect(() => {
    if (userProfile) {
      getProfileOrganisation(userProfile.organisationId);
    }
  }, [userProfile, getProfileOrganisation]);

  useEffect(() => {
    if (userProfile && userOrganisation) {
      const name = `${userProfile.firstName} ${userProfile.lastName}`;
      // Once the organisation has loaded we have the real organisation name,
      // so set the full user context including the organisation.name tag.
      setSentryUser({
        email: userProfile.email,
        id: userProfile.id,
        organisationId: userProfile.organisationId,
        organisationName: userOrganisation.organisationName,
        username: `${userOrganisation.organisationName} - ${name}`
      });
    }
  }, [userOrganisation, userProfile]);

  useEffect(() => {
    if (userOrganisation && userProfile) {
      const name = `${userProfile.firstName} ${userProfile.lastName}`;
      update({
        company: {
          id: userOrganisation?.id,
          name: userOrganisation?.organisationName
        },
        email: userProfile?.email,
        id: userProfile?.id,
        name: name,
        organisationId: userProfile?.organisationId
      });
    }
  }, [userOrganisation, userProfile]);

  return (
    <ProfileContext.Provider
      value={{
        createUserProfile,
        isAuthProfileLoading: isAuthLoading || isOrganisationLoading,
        setUpUserProfileAndOrg,
        setUserOrganisation,
        updateUserOrganisation,
        updateUserProfile,
        userOrganisation,
        userProfile
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

const useProfileContext = () => useContext(ProfileContext);

export { ProfileProvider, useProfileContext };
